// Vendor boundary for product analytics. Consumers import `useAnalytics` from here, not posthog-js/react.
import { useAuth0 } from "@auth0/auth0-react";
import { FeatureFlags } from "@utils/FeatureFlags/generated-flags";
import { APP_ENV, ENV_NAMES } from "@utils/utils";
import { useFlags } from "launchdarkly-react-client-sdk";
import { DomAutocaptureEvents } from "posthog-js";
import { PostHogProvider, usePostHog } from "posthog-js/react";
import { PropsWithChildren, useEffect } from "react";

import { useAdminData } from "@providers/AdminDataProvider";

export const useAnalytics = usePostHog;

// Strips query params from $current_url on every event so URLs with clinical
// free-text (e.g. ?search=<learner-name>) don't reach PostHog. Exported for
// direct unit testing; live SDK invokes it via POSTHOG_OPTIONS.sanitize_properties.
export const sanitizeAnalyticsProperties = (properties: Record<string, unknown>): Record<string, unknown> => {
  if (typeof properties.$current_url === "string") {
    properties.$current_url = properties.$current_url.split("?")[0];
  }
  return properties;
};

const POSTHOG_OPTIONS = {
  api_host: "https://us.i.posthog.com",
  autocapture: { dom_event_allowlist: ["click", "submit"] as DomAutocaptureEvents[] },
  capture_pageview: true,
  disable_toolbar: true,
  mask_all_element_attributes: true,
  person_profiles: "identified_only" as const,
  secure_cookie: true,
  // Session recording uses class-based PHI masking after the ph-no-capture
  // audit (PROA-1411). maskTextClass masks any element bearing ph-no-capture
  // in replays; maskAllInputs covers all <input>/<textarea> automatically.
  // Project setting at https://us.posthog.com/project/353733/settings/environment-recordings
  // must also have "Record sessions" toggled ON for any recordings to flow.
  session_recording: {
    maskAllInputs: true,
    maskTextClass: "ph-no-capture",
    minimumDurationMilliseconds: 30_000,
  },
  sanitize_properties: sanitizeAnalyticsProperties,
  loaded: (ph: { register: (props: Record<string, unknown>) => void; debug: () => void }) => {
    ph.register({
      environment: APP_ENV,
      app_version: import.meta.env.VITE_COMMIT_HASH ?? "unknown",
    });
    if (import.meta.env.VITE_POSTHOG_DEBUG === "true") ph.debug();
  },
};

export const AnalyticsProvider: React.FC<PropsWithChildren> = ({ children }) => {
  const apiKey = import.meta.env.VITE_POSTHOG_KEY;
  const flags = useFlags<FeatureFlags>();
  const isEnabled = !!flags["analytics-enabled"];

  // avoid initializing posthog when running the local dev server or api key isn't set
  if (!apiKey || (import.meta.env.DEV && !import.meta.env.VITEST)) {
    if (!apiKey && APP_ENV !== ENV_NAMES.LOCAL) {
      console.warn("Analytics: VITE_POSTHOG_KEY missing");
    }
    return <>{children}</>;
  }

  return (
    <PostHogProvider apiKey={apiKey} options={{ ...POSTHOG_OPTIONS, opt_out_capturing_by_default: !isEnabled }}>
      <AnalyticsCaptureGate isEnabled={isEnabled} />
      <AnalyticsIdentify isEnabled={isEnabled}>{children}</AnalyticsIdentify>
    </PostHogProvider>
  );
};

// Keep PostHog mounted across flag toggles so the user's distinct_id and
// session stay stable; only flip capturing on/off rather than tearing the
// provider down (which would mint a new anonymous distinct_id every flip).
const AnalyticsCaptureGate: React.FC<{ isEnabled: boolean }> = ({ isEnabled }) => {
  const analytics = useAnalytics();
  useEffect(() => {
    if (!analytics) return;
    if (isEnabled) analytics.opt_in_capturing();
    else analytics.opt_out_capturing();
  }, [analytics, isEnabled]);
  return null;
};

const AnalyticsIdentify: React.FC<PropsWithChildren<{ isEnabled: boolean }>> = ({ children, isEnabled }) => {
  const analytics = useAnalytics();
  const { user } = useAuth0();
  const { orgId, orgName, roles } = useAdminData();
  const role = roles[0];

  useEffect(() => {
    if (!analytics || !user || !orgId || !isEnabled) return;
    analytics.identify(user.sub, {
      org_id: orgId,
      org_name: orgName,
      role,
      roles,
      name: user.name,
      email: user.email,
    });
    analytics.group("company", orgId, { name: orgName });
  }, [analytics, user, orgId, role, roles, orgName, isEnabled]);

  useEffect(() => {
    if (!user && analytics) analytics.reset();
  }, [user, analytics]);

  return <>{children}</>;
};
